// Shared slab/tier range validation for the Shift Type and Shift Leader Commission modals.
// Rows must be ascending and contiguous (next min = previous max + 1), whole-number min/max,
// non-negative values, a max on every row except the last, and an open-ended last row.

const V = 'messageCenterShiftTypes.validation';

const isWholeNumber = (v) => /^\d+$/.test(String(v));
const isNonNegativeNumber = (v) => v !== '' && !isNaN(v) && parseFloat(v) >= 0;

export const validateRanges = (rows, { valueField, prefix, t, maxValue }) => {
  const errors = {};

  rows.forEach((row, i) => {
    const key = (field) => `${prefix}.${i}.${field}`;
    const isLast = i === rows.length - 1;
    const prev = rows[i - 1];
    const min = parseFloat(row.minValue);
    const max = parseFloat(row.maxValue);

    if (row.minValue === '' || isNaN(row.minValue)) {
      errors[key('minValue')] = t(`${V}.slabMinValueInvalid`);
    } else if (min < 0) {
      errors[key('minValue')] = t(`${V}.minNegative`);
    } else if (!isWholeNumber(row.minValue)) {
      errors[key('minValue')] = t(`${V}.wholeNumber`);
    } else if (prev && isWholeNumber(prev.maxValue)) {
      const expected = parseFloat(prev.maxValue) + 1;
      if (min < expected) {
        errors[key('minValue')] = t(`${V}.outOfOrder`, { value: expected });
      } else if (min > expected) {
        errors[key('minValue')] = t(`${V}.gap`, { value: expected });
      }
    }

    if (!isLast) {
      if (row.maxValue === '') {
        errors[key('maxValue')] = t(`${V}.maxRequired`);
      } else if (isNaN(row.maxValue) || max < 0) {
        errors[key('maxValue')] = t(`${V}.maxNegative`);
      } else if (!isWholeNumber(row.maxValue)) {
        errors[key('maxValue')] = t(`${V}.wholeNumber`);
      } else if (!isNaN(min) && max <= min) {
        errors[key('maxValue')] = t(`${V}.maxGreater`);
      }
    } else if (row.maxValue !== '') {
      errors[key('maxValue')] = t(`${V}.lastOpenEnded`);
      errors[key('row')] = t(`${V}.lastOpenEnded`);
    }

    const value = row[valueField];
    if (value !== '' && !isNaN(value) && parseFloat(value) < 0) {
      errors[key(valueField)] = t(`${V}.rateNegative`);
    } else if (!isNonNegativeNumber(value)) {
      errors[key(valueField)] = t(`${V}.rateRequired`);
    } else if (maxValue != null && parseFloat(value) > maxValue) {
      errors[key(valueField)] = t(`${V}.rateMax`);
    }
  });

  return errors;
};

const BLOCKED_KEYS = ['-', '+', 'e', 'E', ','];

export const blockInvalidNumberKeys = (e) => {
  if (BLOCKED_KEYS.includes(e.key)) e.preventDefault();
};

export const blockNonIntegerKeys = (e) => {
  if (BLOCKED_KEYS.includes(e.key) || e.key === '.') e.preventDefault();
};

export const sanitizeNumber = (value, allowDecimal = true) => {
  const cleaned = String(value).replace(allowDecimal ? /[^\d.]/g : /\D/g, '');
  if (!allowDecimal) return cleaned;
  const [whole, ...rest] = cleaned.split('.');
  return rest.length ? `${whole}.${rest.join('')}` : whole;
};
